// ChatView — the ONE chat window both INOVIU assistants draw: the :8002 assistant
// (AssistantSection) imports it, and the :8765 Onshape panel mounts the same code from
// /chat-ui.js (src/web/chat-ui.jsx). Alex, 2026-10-11: the two chats must look the same
// and upgrade the same way, while each keeps what it is for. So this file owns only the
// LOOK: messages, the composer card, the model picker under the input. Where messages
// come from, what a send does and what the model knows all stay with each app.
//
// Props are plain data and callbacks only, never React elements: the :8765 panel runs
// its own copy of React, and an element from one React cannot render in another.
//   messages      [{ id?, role: 'user'|'assistant', content, images? }] — assistant text is
//                 markdown; images [{ media_type, data }] (base64) show above a user's text
//   busy          a reply is on its way (shows "Thinking…" until the reply has text)
//   error         string shown above the composer
//   placeholder, emptyHint, disabled
//   onSend(text, model, images)  may return a promise; a rejection puts the text and the
//                 pictures back in the box. images is [] unless allowImages.
//   onStop()      while busy, Send becomes Stop (and Esc stops). The app cancels its request
//                 and rejects the onSend promise, so what was typed comes back to edit.
//   allowImages   paste or drop pictures into the box (downscaled here, before sending)
//   models        [{ id, display_name }] for the picker; defaultModel — the app's default
//   controls      [{ kind: 'toggle', label, checked, onChange, title }
//                  | { kind: 'button', label, onClick, title }] — left end of the bar
//   transform(content) → markdown, applied to assistant text (the :8002 citation links)
//   showUser(content)  → text shown in a user bubble
//
// The picker rule: a manual pick is kept only while the app's default is unchanged.
// When INOVIU_Core moves to a new model (src/assistant.js), every browser follows it.
import { useEffect, useRef, useState } from 'react';
import Markdown from './Markdown.jsx';
import SelectCell from './SelectCell.jsx';
import { BTN_PRIMARY_OUTLINE, BTN_SECONDARY, BTN_DISABLED, BTN_GLYPH } from '../theme-tokens.js';
import { DELETE } from '../icons.js';
import { shrinkImage } from '../image-shrink.js';

const LS_PICK = 'chat_model';
const LS_PICK_DEFAULT = 'chat_model_default';

function readPick(defaultModel, models) {
  try {
    const pick = localStorage.getItem(LS_PICK);
    const kept = pick && localStorage.getItem(LS_PICK_DEFAULT) === defaultModel
      && models.some(m => m.id === pick);
    if (kept) return pick;
    localStorage.removeItem(LS_PICK);
    localStorage.removeItem(LS_PICK_DEFAULT);
  } catch { /* storage blocked: the default it is */ }
  return defaultModel;
}

function costTier(id) {
  if (id.includes('haiku')) return 'cheapest, fastest';
  if (id.includes('sonnet')) return 'standard cost and speed';
  if (id.includes('opus') || id.includes('fable')) return 'premium, most capable';
  return '';
}

// Pictures: 1568 px on the long edge is what the model reads anyway — anything larger is
// shrunk by the API, so shrinking here costs nothing and keeps the request small.
const MAX_IMAGES = 5;
const MAX_EDGE = 1568;
const KEEP_BYTES = 1_500_000;

// The redraw itself is shared with the project-logo drop (image-shrink.js).
const toImage = (file) => shrinkImage(file, { maxEdge: MAX_EDGE, keepBytes: KEEP_BYTES });

const imageSrc = (img) => `data:${img.media_type};base64,${img.data}`;

const CARD = {
  background: 'var(--th-row-even)', border: '1px solid var(--th-border-subtle)',
  borderRadius: 4,
};
const BUBBLE = {
  maxWidth: '82%', padding: '8px 11px', borderRadius: 14, fontSize: 'var(--fs-small)',
  lineHeight: 1.55, wordBreak: 'break-word', color: 'var(--text-primary)',
};
const BAR_TEXT = { fontSize: 'var(--fs-micro)', color: 'var(--th-text-secondary)' };

export default function ChatView({
  messages = [], busy = false, error = null, placeholder = 'Ask a question…',
  emptyHint = 'Ask a question below to start.', disabled = false, onSend, onStop = null,
  allowImages = false, models = [], defaultModel = null, controls = [],
  transform = (s) => s, showUser = (s) => s,
}) {
  const [draft, setDraft] = useState('');
  const [images, setImages] = useState([]);
  const [imageNote, setImageNote] = useState(null);
  const [model, setModel] = useState(defaultModel);
  const [focused, setFocused] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (defaultModel) setModel(readPick(defaultModel, models));
  }, [defaultModel, models]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const pickModel = (id) => {
    setModel(id);
    try {
      localStorage.setItem(LS_PICK, id);
      localStorage.setItem(LS_PICK_DEFAULT, defaultModel || '');
    } catch { /* not remembered, still used for this session */ }
  };

  // Esc stops, like Claude. On the window, because the box is disabled while busy and a
  // disabled textarea receives no keys.
  useEffect(() => {
    if (!busy || !onStop) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); onStop(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onStop]);

  const addFiles = async (files) => {
    const picked = [...files].filter(f => f.type.startsWith('image/'));
    if (!picked.length) return false;
    const room = MAX_IMAGES - images.length;
    setImageNote(picked.length > room ? `At most ${MAX_IMAGES} images per message.` : null);
    const added = [];
    for (const f of picked.slice(0, Math.max(0, room))) {
      try { added.push(await toImage(f)); } catch { setImageNote('That image could not be read.'); }
    }
    if (added.length) setImages(prev => [...prev, ...added].slice(0, MAX_IMAGES));
    return true;
  };
  const onPaste = (e) => {
    if (!allowImages) return;
    const files = [...(e.clipboardData?.files || [])];
    if (files.some(f => f.type.startsWith('image/'))) { e.preventDefault(); addFiles(files); }
  };
  const onDrop = (e) => {
    if (!allowImages || busy || disabled) return;
    if (e.dataTransfer?.files?.length) { e.preventDefault(); addFiles(e.dataTransfer.files); }
  };

  const canSend = !busy && !disabled && (draft.trim().length > 0 || images.length > 0);
  const send = () => {
    if (!canSend) return;
    const text = draft.trim();
    const sent = images;
    setDraft('');
    setImages([]);
    setImageNote(null);
    Promise.resolve(onSend?.(text, model, sent))
      .catch(() => { setDraft(text); setImages(sent); })
      .finally(() => inputRef.current?.focus());
  };
  const stopping = busy && !!onStop;

  const last = messages[messages.length - 1];
  const thinking = busy && !(last && last.role === 'assistant' && last.content);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 8, padding: '0 8px 8px' }}>
      <div ref={listRef} style={{
        ...CARD, flex: 1, minHeight: 0, overflowY: 'auto', padding: 12,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {messages.length === 0 && !busy && (
          <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-hint)', textAlign: 'center', padding: '24px 16px' }}>
            {emptyHint}
          </div>
        )}
        {messages.map((m, i) => {
          if (m.role === 'assistant' && !m.content) return null;
          return m.role === 'user' ? (
            <div key={m.id ?? i} style={{
              ...BUBBLE, alignSelf: 'flex-end', whiteSpace: 'pre-wrap',
              background: 'var(--th-row-selected)', borderBottomRightRadius: 4,
            }}>
              {m.images?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: m.content ? 6 : 0 }}>
                  {m.images.map((img, k) => (
                    <img key={k} src={imageSrc(img)} alt={`Image ${k + 1}`}
                         style={{ maxHeight: 120, maxWidth: '100%', borderRadius: 4, display: 'block' }} />
                  ))}
                </div>
              )}
              {showUser(m.content)}
            </div>
          ) : (
            <div key={m.id ?? i} style={{
              ...BUBBLE, alignSelf: 'flex-start',
              background: 'var(--surface-sunken)', borderBottomLeftRadius: 4,
            }}>
              <Markdown text={transform(m.content)} size="small" style={{ marginBottom: -6 }} />
            </div>
          );
        })}
        {thinking && (
          <div style={{ ...BUBBLE, alignSelf: 'flex-start', background: 'var(--surface-sunken)', color: 'var(--th-text-hint)' }}>
            Thinking…
          </div>
        )}
      </div>

      {error && (
        <div style={{
          ...CARD, flexShrink: 0, padding: '6px 10px', fontSize: 'var(--fs-small)',
          color: 'var(--status-deprecated)', background: 'var(--surface-error)',
        }}>
          {error}
        </div>
      )}

      <div onDragOver={(e) => { if (allowImages) e.preventDefault(); }} onDrop={onDrop} style={{
        ...CARD, flexShrink: 0, display: 'flex', flexDirection: 'column',
        borderColor: focused ? 'var(--border-strong)' : 'var(--th-border-subtle)',
      }}>
        {images.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px 0' }}>
            {images.map((img, k) => (
              <div key={k} style={{ position: 'relative' }}>
                <img src={imageSrc(img)} alt={`Image ${k + 1} to send`}
                     style={{ height: 56, maxWidth: 120, objectFit: 'cover', borderRadius: 4, display: 'block', border: '1px solid var(--th-border-subtle)' }} />
                <button
                  title="Remove this image"
                  aria-label={`Remove image ${k + 1}`}
                  disabled={busy}
                  onClick={() => setImages(prev => prev.filter((_, j) => j !== k))}
                  style={{ ...BTN_GLYPH, position: 'absolute', top: 0, right: 0, background: 'var(--th-row-even)', borderRadius: 4 }}
                >{DELETE}</button>
              </div>
            ))}
          </div>
        )}
        {imageNote && <div style={{ ...BAR_TEXT, padding: '4px 10px 0' }}>{imageNote}</div>}
        <textarea
          ref={inputRef}
          value={draft}
          rows={3}
          placeholder={placeholder}
          disabled={disabled || busy}
          title={allowImages ? 'Paste or drop an image to show it to the assistant.' : undefined}
          onChange={(e) => setDraft(e.target.value)}
          onPaste={onPaste}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          style={{
            width: '100%', boxSizing: 'border-box', minHeight: 64, padding: '8px 10px 4px',
            border: 'none', outline: 'none', resize: 'none', background: 'transparent',
            fontSize: 'var(--fs-small)', fontFamily: 'inherit', lineHeight: 1.4,
            color: disabled ? 'var(--th-text-disabled)' : 'var(--text-primary)',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px 6px 8px' }}>
          {controls.map((c, i) => (c.kind === 'toggle' ? (
            <label key={i} title={c.title} style={{ ...BAR_TEXT, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={!!c.checked} disabled={busy}
                     onChange={(e) => c.onChange?.(e.target.checked)} />
              {c.label}
            </label>
          ) : (
            <button key={i} title={c.title} disabled={busy} onClick={c.onClick}
                    style={{ ...BTN_SECONDARY, fontSize: 'var(--fs-micro)', padding: '3px 8px', ...(busy ? BTN_DISABLED : null) }}>
              {c.label}
            </button>
          )))}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            {models.length > 0 && model ? (
              <div style={{ width: 110 }} title={`${costTier(model)} (per-token API billing)`}>
                <SelectCell
                  value={model}
                  onChange={pickModel}
                  options={models.map(m => ({ value: m.id, label: m.display_name }))}
                  title="Model: Haiku is cheapest, Sonnet standard, Opus and Fable premium"
                  style={{ fontSize: 'var(--fs-micro)', padding: '2px 4px', color: 'var(--th-text-secondary)' }}
                />
              </div>
            ) : (model && <span style={BAR_TEXT}>{model}</span>)}
            {stopping ? (
              <button
                onClick={onStop}
                title="Stop the answer (Esc). Your message comes back to edit."
                style={{ ...BTN_PRIMARY_OUTLINE, flexShrink: 0 }}
              >
                Stop
              </button>
            ) : (
              <button
                onClick={send}
                disabled={!canSend}
                title="Send (Enter). Shift+Enter for a new line."
                style={{ ...BTN_PRIMARY_OUTLINE, flexShrink: 0, ...(canSend ? null : BTN_DISABLED) }}
              >
                Send
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
